import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { router, usePage } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { audioManager } from '@/lib/audio'
import { undoHistory } from '@/lib/history-api'
import { withProgressToast } from '@/lib/progress-toast'
import { workflowActivityStore } from '@/lib/workflow-activity-store'
import { isWorkflowInProgress as checkWorkflowInProgress, isWorkflowBlocking as checkWorkflowBlocking } from '@/lib/workflow-utils'
import type {
  Conversation,
  Message,
  MessageAttachment,
  GroupedTodos,
  ChannelEvent,
  HistoryEntry,
  OptimisticAttachment,
  OrganizationBilling,
  ActiveWorkflow
} from '@/types/conversations'

/**
 * Calculate which messages should be collapsed based on turn_summary messages.
 * The turn_data contains explicit collapsed message IDs from the backend.
 */
function calculateCollapsedMessages(messages: Message[]): Set<number> {
  const collapsed = new Set<number>()

  const collapseSummaries = messages.filter(m => m.message_type === 'turn_summary')

  for (const summaryMsg of collapseSummaries) {
    if (!summaryMsg.turn_data?.collapsedMessageIds) continue

    summaryMsg.turn_data.collapsedMessageIds.forEach((id: number) => {
      collapsed.add(id)
    })
  }

  return collapsed
}

const WORKER_PROVISIONING_TIMEOUT_MS = 5 * 60 * 1000

interface ConversationStateOptions {
  initialConversation: Conversation
  initialMessages: Message[]
  initialTodos: GroupedTodos
  initialProcessing: boolean
  initialActiveWorkflow?: ActiveWorkflow | null
  initialHasMore?: boolean
  projectId: number
  initialProject?: {
    id: number
    name: string
    display_name?: string
    mobile_included?: boolean
    instance_size?: string
    instance_specs?: { memory_gb: number; max_workers: number }
  }
  initialConversations?: Conversation[]
  initialHistoryEntries?: HistoryEntry[]
  initialOrganization?: OrganizationBilling
  initialOrphanedMessageIds?: number[]
  initialCanUndoMessageId?: number | null
  initialCodingWorkers?: Array<{ id: number; name: string; status: string; preview_url: string; active_task_id: number | null }>
}

/**
 * Hook to manage conversation state based on ActionCable events
 * Handles message streaming, todos updates, activity indicators, git status, etc.
 */
export function useConversationState({
  initialConversation,
  initialMessages,
  initialTodos,
  initialProcessing,
  initialActiveWorkflow = null,
  initialHasMore = false,
  projectId,
  initialProject,
  initialConversations = [],
  initialHistoryEntries = [],
  initialOrganization,
  initialOrphanedMessageIds = [],
  initialCanUndoMessageId = null,
  initialCodingWorkers = []
}: ConversationStateOptions) {
  const { auth } = usePage<{ auth: { user: { name: string; avatar_url?: string | null } } }>().props
  const { t } = useTranslation('conversations')

  const [conversation, setConversation] = useState(initialConversation)
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [groupedTodos, setGroupedTodos] = useState(initialTodos)
  const [processing, setProcessing] = useState(initialProcessing)
  // Don't seed activity history — if there's real build activity, the pilot sends ACTIVITY chunks.
  // Seeding with 'Building' on page load caused false activity badges for stale PM processing state.
  const [activityHistory, setActivityHistory] = useState<string[]>([])
  const currentActivity = activityHistory.length > 0 ? activityHistory[activityHistory.length - 1] : null
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [isAcceptingChanges, setIsAcceptingChanges] = useState(false)
  const [isDiscardingChanges, setIsDiscardingChanges] = useState(false)
  const [isInterrupting, setIsInterrupting] = useState(false)
  const [isRetryingWorkflow, setIsRetryingWorkflow] = useState(false)
  const [activeWorkflow, setActiveWorkflow] = useState<ActiveWorkflow | null>(initialActiveWorkflow)
  const [project, setProject] = useState(initialProject)
  const [memoryWarning, setMemoryWarning] = useState<{
    memory_percent: number
    current_size: string
    current_memory_gb: number
    suggested_size: string | null
    suggested_memory_gb: number | null
    instance_name: string
  } | null>(null)

  // Detect if processing is due to a non-blocking workflow (allows message input)
  const isNonBlockingWorkflowActive = useMemo(() => {
    if (!processing) return false
    return checkWorkflowInProgress(activeWorkflow) && !checkWorkflowBlocking(activeWorkflow)
  }, [processing, activeWorkflow])

  // Sync project state when props change (e.g., after router.reload())
  useEffect(() => {
    if (initialProject) {
      setProject(initialProject)
    }
  }, [
    initialProject?.id,
    initialProject?.name,
    initialProject?.mobile_included,
    initialProject?.instance_size,
    initialProject?.instance_specs?.max_workers
  ])

  const [conversations, setConversations] = useState<Conversation[]>(initialConversations)
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>(initialHistoryEntries || [])
  const [organization, setOrganization] = useState(initialOrganization)
  const [orphanedMessageIds, setOrphanedMessageIds] = useState<number[]>(initialOrphanedMessageIds)
  const [codingWorkers, setCodingWorkers] = useState(initialCodingWorkers)
  const [provisioningWorkers, setProvisioningWorkers] = useState<string[]>([])
  const provisioningTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const managedAgentDeploymentToastRef = useRef<string | null>(null)
  const managedAgentSessionToastRef = useRef<string | null>(null)

  const clearProvisioningTimeout = useCallback((name: string) => {
    const timeout = provisioningTimeoutsRef.current[name]
    if (timeout) {
      clearTimeout(timeout)
      delete provisioningTimeoutsRef.current[name]
    }
  }, [])

  const trackProvisioningTimeout = useCallback((name: string) => {
    if (provisioningTimeoutsRef.current[name]) return

    provisioningTimeoutsRef.current[name] = setTimeout(() => {
      delete provisioningTimeoutsRef.current[name]
      setProvisioningWorkers(prev => prev.filter(workerName => workerName !== name))
      toast.error(t('taskBoard.parallelProvisionTimedOut'), {
        description: name
      })
    }, WORKER_PROVISIONING_TIMEOUT_MS)
  }, [t])

  const addProvisioningWorkers = useCallback((names: string[]) => {
    const existingWorkerNames = new Set(codingWorkers.map(worker => worker.name))
    const namesToTrack = names.filter(name => !existingWorkerNames.has(name))

    namesToTrack.forEach(trackProvisioningTimeout)
    setProvisioningWorkers(prev => Array.from(new Set([...prev, ...namesToTrack])))
  }, [codingWorkers, trackProvisioningTimeout])

  const removeProvisioningWorkers = useCallback((names: string[]) => {
    const namesToRemove = new Set(names)

    names.forEach(clearProvisioningTimeout)
    setProvisioningWorkers(prev => prev.filter(name => !namesToRemove.has(name)))
  }, [clearProvisioningTimeout])

  useEffect(() => {
    return () => {
      Object.values(provisioningTimeoutsRef.current).forEach(clearTimeout)
      provisioningTimeoutsRef.current = {}
    }
  }, [])

  // Sync organization when props change (e.g., after router.reload() following credit purchase)
  useEffect(() => {
    setOrganization(initialOrganization)
  }, [initialOrganization])

  // Sync historyEntries when props change
  useEffect(() => {
    setHistoryEntries(initialHistoryEntries || [])
  }, [initialHistoryEntries])

  // Sync orphanedMessageIds when props change
  useEffect(() => {
    setOrphanedMessageIds(initialOrphanedMessageIds)
  }, [initialOrphanedMessageIds])

  // Live-updated credentials (from ActionCable when Claude sets up admin)
  const [liveCredentials, setLiveCredentials] = useState<{
    rails_app_password?: string
    rails_app_admin_email?: string
  } | null>(null)

  // Live-updated product spec (from ActionCable when spec is updated)
  const [liveProductSpec, setLiveProductSpec] = useState<{
    content: string
    updated_at: string
  } | null>(null)

  const [nextStepSuggestions, setNextStepSuggestions] = useState<Array<{ name: string; prompt: string }>>(
    initialConversation.next_step_suggestions || []
  )

  // PM browser screenshots — shown inline in chat while PM browses
  const [pmBrowserScreenshots, setPmBrowserScreenshots] = useState<Array<{ image: string; mediaType: string; caption: string; timestamp: number }>>([])

  // Track which user message can be undone (null = no message can be undone)
  const [canUndoMessageId, setCanUndoMessageId] = useState<number | null>(initialCanUndoMessageId)

  // Track rewinding/undoing state (blocks input and other operations)
  const [isRewinding, setIsRewinding] = useState(false)

  // State for collapsed message IDs (messages hidden under "View full response" link)
  // Initialize with collapsed state based on existing messages
  const [collapsedMessageIds, setCollapsedMessageIds] = useState<Set<number>>(() => {
    return calculateCollapsedMessages(initialMessages)
  })

  // State for turns that user has manually expanded (overrides collapse)
  // Uses user message IDs (stable across pagination) instead of indices
  const [expandedTurns, setExpandedTurns] = useState<Set<number>>(new Set())

  // Pagination state for message history
  const [hasMoreMessages, setHasMoreMessages] = useState(initialHasMore)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)

  // Store stable IDs in refs to avoid recreating callbacks
  const projectIdRef = useRef(projectId)
  const conversationIdRef = useRef(initialConversation.id)

  // Throttle message_chunk updates to avoid excessive re-renders during streaming
  // Stores the latest chunk data per message ID, flushed every CHUNK_THROTTLE_MS
  const CHUNK_THROTTLE_MS = 30
  const pendingChunksRef = useRef<Map<number, any>>(new Map())
  const chunkFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flushPendingChunks = useCallback(() => {
    const pending = pendingChunksRef.current
    if (pending.size === 0) return

    const entries = Array.from(pending.entries())
    pending.clear()
    chunkFlushTimerRef.current = null

    setMessages(prev => {
      let updated = prev
      for (const [messageId, chunkData] of entries) {
        const existingIndex = updated.findIndex(m => m.id === messageId)
        if (existingIndex >= 0) {
          if (updated === prev) updated = [...prev]
          updated[existingIndex] = {
            ...updated[existingIndex],
            content: chunkData.content,
            message_type: chunkData.message_type || updated[existingIndex].message_type,
            status: chunkData.status || updated[existingIndex].status,
            task_id: chunkData.task_id ?? updated[existingIndex].task_id,
            questions_data: chunkData.questions_data || updated[existingIndex].questions_data,
            questions_answered: chunkData.questions_answered ?? updated[existingIndex].questions_answered,
            questions_answers: chunkData.questions_answers || updated[existingIndex].questions_answers,
            questions_superseded: chunkData.questions_superseded ?? updated[existingIndex].questions_superseded,
            confirmation_button_data: chunkData.confirmation_button_data || updated[existingIndex].confirmation_button_data,
            confirmation_clicked: chunkData.confirmation_clicked ?? updated[existingIndex].confirmation_clicked,
            source: chunkData.source || updated[existingIndex].source
          }
        } else {
          if (updated === prev) updated = [...prev]
          updated.push({
            ...chunkData,
            id: messageId,
            message_type: chunkData.message_type || 'regular',
            status: chunkData.status || 'completed'
          } as Message)
        }
      }
      return updated
    })
  }, [])

  // Cleanup chunk flush timer on unmount
  useEffect(() => {
    return () => {
      if (chunkFlushTimerRef.current) {
        clearTimeout(chunkFlushTimerRef.current)
      }
    }
  }, [])

  // Debounce refetchData to prevent race conditions during operations
  const lastRefetchRef = useRef<number>(0)
  const REFETCH_COOLDOWN = 2000 // 2 seconds

  // Note: Timeout monitoring is now handled by backend (MonitorStuckMessagesJob)
  // No need for frontend timeout logic

  // Recalculate collapsed messages when messages change
  useEffect(() => {
    const collapsed = calculateCollapsedMessages(messages)
    setCollapsedMessageIds(collapsed)
  }, [messages])

  // Restore git operation states on mount based on git_operation_status
  useEffect(() => {
    if (initialConversation.git_operation_status === 'accepting') {
      setIsAcceptingChanges(true)
    } else if (initialConversation.git_operation_status === 'discarding') {
      setIsDiscardingChanges(true)
    }
  }, [initialConversation.git_operation_status])

  useEffect(() => {
    projectIdRef.current = projectId
    conversationIdRef.current = conversation.id
  }, [projectId, conversation.id])

  // Sync conversation state when prop updates (e.g., from Inertia partial reload)
  useEffect(() => {
    setConversation(initialConversation)
    setNextStepSuggestions(initialConversation.next_step_suggestions || [])
  }, [initialConversation])

  /**
   * Handle incoming events from ActionCable
   */
  const handleChannelEvent = useCallback((event: ChannelEvent) => {
    console.log('📡 ActionCable event:', event.type, event.data)

    switch (event.type) {
      case 'message_chunk':
        // Throttle streaming chunks to reduce re-renders (batches rapid updates)
        pendingChunksRef.current.set(event.data.message_id, event.data)
        if (!chunkFlushTimerRef.current) {
          chunkFlushTimerRef.current = setTimeout(flushPendingChunks, CHUNK_THROTTLE_MS)
        }
        break

      case 'message_complete': {
        // Flush any pending chunks before applying complete (ensures latest content is in state)
        if (pendingChunksRef.current.size > 0) {
          if (chunkFlushTimerRef.current) {
            clearTimeout(chunkFlushTimerRef.current)
            chunkFlushTimerRef.current = null
          }
          flushPendingChunks()
        }

        // Update message with complete data (includes turn_data for collapse_summary messages)
        const applyMessageComplete = () => {
          setMessages(prev => {
            const existingIndex = prev.findIndex(m => m.id === event.data.id)
            if (existingIndex >= 0) {
              // Update existing message with full data from broadcast
              const updated = [...prev]
              updated[existingIndex] = {
                ...updated[existingIndex],
                ...event.data,
              }
              return updated
            } else {
              // Add new message (e.g., collapse_summary created on backend)
              return [...prev, event.data as Message]
            }
          })
        }

        // Delay discussion summary so user sees the confirmed button before collapse
        if (event.data.message_type === 'discussion_summary') {
          setTimeout(applyMessageComplete, 0)
        } else {
          applyMessageComplete()
        }
        break
      }

      case 'user_message_sent': {
        // Another user/window sent a message — add it if we don't already have it
        const userMsg = event.data as Message
        setMessages(prev => {
          if (prev.some(m => m.id === userMsg.id)) return prev
          // Sender's window: optimistic message has negative ID, broadcast has real ID.
          // Match by content+role to avoid duplicates while HTTP response is in flight.
          if (prev.some(m => m.id < 0 && m.role === 'user' && m.content === userMsg.content)) return prev
          return [...prev, userMsg]
        })
        break
      }

      case 'todos_update':
        // Update todos
        setGroupedTodos(event.data.grouped)
        break

      case 'activity_start':
        // Append to rolling activity history (keep last 4, skip consecutive duplicates)
        setActivityHistory(prev => {
          if (prev.length > 0 && prev[prev.length - 1] === event.data.activity) {
            return prev
          }
          const next = [...prev, event.data.activity]
          return next.slice(-4)
        })
        break

      case 'activity_end':
        // Don't clear — history persists while processing
        break

      case 'pm_browser_screenshot':
        setPmBrowserScreenshots(prev => [
          ...prev,
          {
            image: event.data.image,
            mediaType: event.data.media_type || 'image/jpeg',
            caption: event.data.caption || '',
            timestamp: Date.now()
          }
        ])
        break

      case 'processing_status':
        // Update processing state
        setProcessing(event.data.processing)

        // Reset retry/interrupt state when processing starts (new processing = stale state)
        if (event.data.processing) {
          setIsRetryingWorkflow(false)
          setIsInterrupting(false)
        }

        // Play completion sound, clear activity history, and update undo info when processing ends
        if (!event.data.processing) {
          audioManager.playCompletionSound()
          setActivityHistory([])
          setPmBrowserScreenshots([])
          setCanUndoMessageId(event.data.can_undo_message_id || null)
        }
        break

      case 'git_status':
        // Update conversation git status
        setConversation(prev => ({
          ...prev,
          has_pending_changes: event.data.has_pending_changes,
          can_accept_changes: event.data.can_accept,
          can_discard_changes: event.data.can_discard
        }))
        break

      case 'git_operation_complete':
        // Git operation completed (accept/discard)
        setConversation(prev => ({
          ...prev,
          status: event.data.status,
          git_operation_status: event.data.git_operation_status
        }))

        // Clear processing states
        setIsAcceptingChanges(false)
        setIsDiscardingChanges(false)

        // Show success toast
        if (event.data.action === 'accept') {
          toast.success('Feature saved successfully!', {
            description: 'Your changes have been added to the project.'
          })
        } else if (event.data.action === 'discard') {
          toast.success('Feature discarded successfully!', {
            description: 'All changes have been discarded.'
          })
        }

        // Redirect after short delay
        setTimeout(() => {
          router.visit(`/projects/${projectIdRef.current}`)
        }, 2000)
        break

      case 'git_operation_error':
        // Git operation failed (accept/discard)
        // Clear processing states
        setIsAcceptingChanges(false)
        setIsDiscardingChanges(false)

        // Show error toast
        toast.error(event.data.user_message, {
          description: 'Please check your connection and try again.'
        })
        break

      case 'interrupt_complete': {
        // Processing was interrupted
        console.log('🛑 Processing interrupted:', event.data)

        // Clear all processing states
        setProcessing(false)
        setIsInterrupting(false)
        setActivityHistory([])

        // Update the message status in the UI
        setMessages(prev =>
          prev.map(m =>
            m.id === event.data.message_id
              ? { ...m, status: 'interrupted' as const }
              : m
          )
        )
        break
      }

      case 'workflow_activity': {
        // Update activity store (doesn't trigger message re-renders)
        workflowActivityStore.setActivity(event.data.step_id, event.data.activity)
        break
      }

      case 'mode_change':
        // Mode changed (planning <-> implementation)
        setConversation(prev => ({
          ...prev,
          mode: event.data.mode,
          plan_approved: event.data.plan_approved,
          can_approve_plan: event.data.can_approve_plan
        }))

        // When plan is approved, update all messages to reflect this
        // This ensures other users see the approval state immediately
        if (event.data.plan_approved) {
          setMessages(prev => prev.map(m => ({
            ...m,
            conversation_plan_approved: true,
            can_be_approved: false
          })))
        }
        // No toast - button visual change is enough feedback
        break

      case 'plan_message':
        // Plan message received
        setMessages(prev => {
          const existingIndex = prev.findIndex(m => m.id === event.data.message_id)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              message_type: 'plan',
              requires_approval: true
            }
            return updated
          } else {
            return [...prev, {
              id: event.data.message_id,
              content: event.data.content,
              role: 'assistant' as const,
              status: 'completed' as const,
              message_type: 'plan' as const,
              requires_approval: true,
              can_be_approved: event.data.can_approve,
              form_skipped: false,
              plan_approved: false,
              conversation_plan_approved: conversation?.plan_approved || false,
              conversation_status: conversation?.status || 'in_progress' as const,
              conversation_id: event.data.conversation_id,
              attachments: [],
              source: 'pilot' as const,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }]
          }
        })
        break

      case 'spec_update':
        // Product spec updated - store in live state for PreviewPane
        console.log('📋 Product spec updated:', event.data.updated_at)
        setLiveProductSpec({
          content: event.data.content,
          updated_at: event.data.updated_at
        })
        toast.info('Product Spec Updated', {
          description: 'The product specification has been updated with the latest changes.'
        })
        break

      case 'credentials_updated':
        // Admin credentials updated (from setup_admin_credentials MCP tool)
        console.log('🔑 Credentials updated:', event.data.rails_app_admin_email)
        setLiveCredentials({
          rails_app_password: event.data.rails_app_password,
          rails_app_admin_email: event.data.rails_app_admin_email
        })
        break

      case 'feature_complete':
        // Feature completed notification (chime already played by processing_status)
        console.log('🎉 Feature completed!', event.data)

        toast.success('Feature Complete! 🎉', {
          description: `All ${event.data.todos_completed} tasks completed successfully.`,
          duration: 5000
        })

        // Set up activity detection to mark notification as seen
        const notificationId = event.data.notification_id
        if (notificationId) {
          const markNotificationAsSeen = async () => {
            try {
              await fetch(`/notifications/${notificationId}/mark_seen`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                }
              })
              console.log('✅ Notification marked as seen:', notificationId)

              // Remove activity listeners after marking as seen
              document.removeEventListener('mousemove', markNotificationAsSeen)
              document.removeEventListener('keydown', markNotificationAsSeen)
              document.removeEventListener('click', markNotificationAsSeen)
            } catch (err) {
              console.error('Failed to mark notification as seen:', err)
            }
          }

          // Listen for user activity
          document.addEventListener('mousemove', markNotificationAsSeen, { once: true })
          document.addEventListener('keydown', markNotificationAsSeen, { once: true })
          document.addEventListener('click', markNotificationAsSeen, { once: true })
        }
        break

      case 'title_updated':
        // Conversation title updated (from async generation)
        if (event.data.title) {
          setConversation(prev => ({
            ...prev,
            title: event.data.title
          }))
          console.log('🏷️ Title updated:', event.data.title)
        }
        break

      case 'project_updated':
        // Project updated (name from async generation, mobile_included from workflow)
        // Merge with existing project to avoid losing other fields
        if (event.data.project) {
          setProject(prev => prev ? ({ ...prev, ...event.data.project }) : prev)
        }
        break

      case 'conversation_updated':
        // Conversation metadata updated (title, status, etc.)
        // Only update specific fields to avoid overwriting with undefined
        if (!event.data?.conversation) {
          console.warn('conversation_updated event missing data.conversation')
          break
        }

        setConversation(prev => {
          const updates: Partial<Conversation> = {}
          if (event.data.conversation.title !== undefined) {
            updates.title = event.data.conversation.title
          }
          if (event.data.conversation.status !== undefined) {
            updates.status = event.data.conversation.status
          }
          return { ...prev, ...updates }
        })
        console.log('📝 Conversation updated:', event.data.conversation)
        break

      case 'credits_update':
        // Update organization credits in real-time
        console.log('💳 Credits updated:', event.data.credits_remaining)
        setOrganization(prev => prev ? {
          ...prev,
          credits_remaining: event.data.credits_remaining
        } : prev)
        break

      case 'conversation_accepted':
        // Update conversations list for history tab
        const acceptedConversation = event.data.conversation
        setConversations(prev => {
          const index = prev.findIndex(c => c.id === acceptedConversation.id)
          if (index >= 0) {
            // Update existing conversation
            const updated = [...prev]
            updated[index] = acceptedConversation
            return updated
          }
          // Add new conversation (shouldn't happen, but handle it)
          return [acceptedConversation, ...prev]
        })
        console.log('✅ Conversation accepted and history updated:', acceptedConversation.id)
        break

      case 'next_step_suggestions':
        // Append new suggestions, keep last 30
        console.log('💡 Received next step suggestions:', event.data.suggestions)
        setNextStepSuggestions(prev => [...(event.data.suggestions || []), ...prev].slice(0, 30))
        break

      case 'forms_skipped':
        // Pending plans/questions were skipped because user sent a new message
        console.log('⏭️ Forms skipped:', event.data.message_ids)
        setMessages(prev => prev.map(m => {
          if (event.data.message_ids.includes(m.id)) {
            return {
              ...m,
              form_skipped: true,
              can_be_approved: false,
              // Also set questions_superseded for backward compatibility
              questions_superseded: m.message_type === 'questions' ? true : m.questions_superseded
            }
          }
          return m
        }))
        break

      case 'import_progress':
        if (event.data.step === 'complete') {
          setProject(prev => prev ? ({ ...prev, import_status: 'imported' }) : prev)
        } else if (event.data.step === 'failed') {
          setProject(prev => prev ? ({ ...prev, import_status: 'import_failed', import_error: event.data.message }) : prev)
        }
        break

      case 'workers_updated':
        if (event.data.coding_workers) {
          setCodingWorkers(event.data.coding_workers)
          const updatedWorkerNames = new Set<string>(
            event.data.coding_workers.map((worker: { name: string }) => worker.name)
          )
          updatedWorkerNames.forEach(clearProvisioningTimeout)
          setProvisioningWorkers(prev => prev.filter(name => !updatedWorkerNames.has(name)))
        }
        break

      case 'workers_provisioning':
        if (event.data.worker_names) {
          const workerNames = event.data.worker_names as string[]
          workerNames.forEach(trackProvisioningTimeout)
          setProvisioningWorkers(prev => Array.from(new Set([...prev, ...workerNames])))
        }
        break

      case 'workers_provisioning_failed':
        if (event.data.worker_names) {
          const failedWorkerNames = new Set<string>(event.data.worker_names as string[])
          failedWorkerNames.forEach(clearProvisioningTimeout)
          setProvisioningWorkers(prev => prev.filter(name => !failedWorkerNames.has(name)))
        } else {
          Object.keys(provisioningTimeoutsRef.current).forEach(clearProvisioningTimeout)
          setProvisioningWorkers([])
        }
        toast.error(event.data.message || t('taskBoard.parallelProvisionFailed'), {
          description: event.data.errors?.join(', ')
        })
        break

      // Task board events — handled by useProjectTasks via Chat.tsx
      case 'task_updated':
      case 'task_created':
      case 'task_activity':
        break

      default:
        console.warn('Unknown event type:', event.type)
    }
  }, [])

  /**
   * Handle incoming project-level events from ProjectChannel
   */
  const handleProjectEvent = useCallback((event: ChannelEvent) => {
    console.log('📡 Project event:', event.type, event.data)

    switch (event.type) {
      case 'deploy_status_update':
        setProject(prev => prev ? ({
          ...prev,
          production_status: event.data.production_status,
          production_url: event.data.production_url,
          has_production_error: event.data.has_production_error,
          production_deployed_at: event.data.production_deployed_at,
          deployed_history_entry_id: event.data.deployed_history_entry_id,
          production_user_email: event.data.production_user_email,
          production_user_password: event.data.production_user_password
        }) : prev)

        if (event.data.production_status === 'deployed') {
          toast.success('App deployed successfully!', {
            description: 'Your app is now live.'
          })
          audioManager.playCompletionSound()
        } else if (event.data.production_status === 'deploy_failed') {
          toast.error('Deployment failed', {
            description: 'Check the error log for details.'
          })
        }
        break

      case 'workflow_progress_update': {
        const wf = event.data as ActiveWorkflow
        setActiveWorkflow(wf)
        // Ensure the message has workflow_id so the historical card renders
        // when activeWorkflow switches to a different workflow
        if (wf.message_id && wf.id > 0) {
          setMessages(prev => prev.map(m =>
            m.id === wf.message_id && !m.workflow_id
              ? { ...m, workflow_id: wf.id }
              : m
          ))
        }
        break
      }

      case 'domain_status_update':
        setProject(prev => prev ? ({
          ...prev,
          custom_domain: event.data.custom_domain,
          custom_domain_status: event.data.custom_domain_status,
          custom_domain_activated_at: event.data.custom_domain_activated_at,
          custom_domain_error: event.data.custom_domain_error,
          domain_purchase: event.data.domain_purchase
        }) : prev)

        if (event.data.custom_domain_status === 'active') {
          toast.success(t('domain.toasts.domainActivated'))
          audioManager.playCompletionSound()
        } else if (event.data.custom_domain_status === 'failed') {
          toast.error(t('domain.toasts.domainFailed'))
        } else if (event.data.custom_domain_status === 'stale') {
          toast.warning(t('domain.toasts.domainStale'))
        }
        break

      case 'seo_status_update':
        setProject(prev => prev ? ({
          ...prev,
          seo_status: event.data.seo_status,
          seo_error: event.data.seo_error,
          seo_metadata: event.data.seo_metadata
        }) : prev)
        break

      case 'security_audit_status_update':
        setProject(prev => prev ? ({
          ...prev,
          security_audit_status: event.data.security_audit_status,
          security_audit_error: event.data.security_audit_error,
          security_audit_metadata: event.data.security_audit_metadata
        }) : prev)
        break

      case 'managed_agent_status_update': {
        setProject(prev => prev ? ({
          ...prev,
          managed_agent_draft: event.data.draft,
          managed_agent_deployment: event.data.deployment
        }) : prev)

        const managedAgentDeployment = event.data.deployment
        const managedAgentSession = managedAgentDeployment?.latest_session
        const sessionCompletedKey = managedAgentSession
          ? `${managedAgentSession.id}:completed:${managedAgentSession.completed_at}`
          : null
        const sessionFailedKey = managedAgentSession
          ? `${managedAgentSession.id}:failed:${managedAgentSession.completed_at}:${managedAgentSession.error_log}`
          : null
        const deploymentCompletedKey = managedAgentDeployment
          ? `${managedAgentDeployment.id}:deployed:${managedAgentDeployment.deployed_at}`
          : null
        const deploymentFailedKey = managedAgentDeployment
          ? `${managedAgentDeployment.id}:failed:${managedAgentDeployment.completed_at}:${managedAgentDeployment.error_log}`
          : null

        if (managedAgentSession?.status === 'completed' && managedAgentSessionToastRef.current !== sessionCompletedKey) {
          managedAgentSessionToastRef.current = sessionCompletedKey
          toast.success('Managed Agent test completed')
          audioManager.playCompletionSound()
        } else if (managedAgentSession?.status === 'failed' && managedAgentSessionToastRef.current !== sessionFailedKey) {
          managedAgentSessionToastRef.current = sessionFailedKey
          toast.error('Managed Agent test failed')
        } else if (managedAgentDeployment?.status === 'deployed' && managedAgentDeploymentToastRef.current !== deploymentCompletedKey) {
          managedAgentDeploymentToastRef.current = deploymentCompletedKey
          toast.success('Managed Agent deployed successfully')
          audioManager.playCompletionSound()
        } else if (managedAgentDeployment?.status === 'failed' && managedAgentDeploymentToastRef.current !== deploymentFailedKey) {
          managedAgentDeploymentToastRef.current = deploymentFailedKey
          toast.error('Managed Agent deployment failed', {
            description: 'Check the deployment details for the error.'
          })
        }
        break
      }

      case 'history_entries_updated':
        setHistoryEntries(event.data.history_entries)
        setCanUndoMessageId(event.data.can_undo_message_id || null)
        if (event.data.orphaned_message_ids) {
          setOrphanedMessageIds(event.data.orphaned_message_ids)
        }
        setProject(prev => {
          if (!prev) return prev
          const updates: Record<string, any> = {}
          if (event.data.current_history_entry_id !== undefined) {
            updates.current_history_entry_id = event.data.current_history_entry_id
          }
          if (event.data.history_entries) {
            updates.history_entries_recent = event.data.history_entries
          }
          return { ...prev, ...updates }
        })
        break

      case 'workflow_activity':
        workflowActivityStore.setActivity(event.data.step_id, event.data.activity)
        break

      case 'memory_warning':
        setMemoryWarning(event.data)
        break

      case 'instance_resized':
        setProject(prev => prev ? ({
          ...prev,
          instance_size: event.data.size,
          instance_specs: { memory_gb: event.data.memory_gb, max_workers: event.data.max_workers }
        }) : prev)
        setMemoryWarning(null)
        toast.success('Sandbox resized successfully', {
          description: `Now running on ${event.data.size} (${event.data.memory_gb} GB RAM)`
        })
        break

      case 'auto_stop_warning':
        toast.warning(t('preview.autoStop.warningTitle'), {
          description: t('preview.autoStop.warningDescription'),
          duration: 30000,
        })
        break

      default:
        console.warn('Unknown project event type:', event.type)
    }
  }, [])

  /**
   * Undo the last change (revert to previous history entry)
   */
  const handleUndo = useCallback(async () => {
    if (isRewinding || !project?.id) return

    setIsRewinding(true)

    try {
      await withProgressToast(
        {
          id: 'undo-operation',
          loadingMessage: 'Undoing last change...',
          loadingDescription: 'Reverting to previous version',
          successMessage: 'Undo complete!',
        },
        () => undoHistory(project!.id)
      )
    } catch {
      // Error already shown by withProgressToast
    } finally {
      setIsRewinding(false)
    }
  }, [project?.id, isRewinding])

  /**
   * Send a new message
   */
  const sendMessage = useCallback(async (content: string, signedBlobIds: string[] = [], selectedElementContext?: any, optimisticAttachments?: OptimisticAttachment[], messageType?: string) => {
    if (!content.trim() || !conversation.can_accept_messages || (processing && !isNonBlockingWorkflowActive)) {
      return
    }

    // Unlock audio on user interaction (required for Chrome autoplay policy)
    audioManager.unlock()

    setIsSendingMessage(true)
    // Suggestions stay — they're valid until a new builder report replaces them

    // Create optimistic message with current user data
    // Use negative timestamp as temporary ID (real IDs from database are always positive)
    const optimisticId = -Date.now()

    // Convert optimistic attachments to MessageAttachment format for preview
    const previewAttachments: MessageAttachment[] = (optimisticAttachments || []).map((att, index) => ({
      id: -(index + 1), // Negative IDs for optimistic attachments
      filename: att.filename,
      content_type: att.content_type,
      byte_size: att.byte_size,
      url: att.previewUrl,
      is_image: att.is_image
    }))

    const optimisticMessage: Message = {
      id: optimisticId,
      conversation_id: conversationIdRef.current,
      content: content.trim(),
      role: 'user',
      status: 'pending',
      message_type: (messageType || 'regular') as Message['message_type'],
      requires_approval: false,
      can_be_approved: false,
      form_skipped: false,
      plan_approved: false,
      conversation_plan_approved: conversation?.plan_approved || false,
      conversation_status: conversation?.status || 'in_progress',
      user: {
        name: auth.user.name,
        avatar_url: auth.user.avatar_url
      },
      attachments: previewAttachments, // Show preview attachments immediately
      selected_element_context: selectedElementContext || null,
      source: 'pilot',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    // Add optimistic message immediately
    setMessages(prev => [...prev, optimisticMessage])

    // Show processing indicator
    setProcessing(true)

    try {
      // Use FormData if signedBlobIds or selectedElementContext are present, otherwise JSON
      const hasSignedBlobIds = signedBlobIds.length > 0
      const hasElementContext = selectedElementContext !== undefined && selectedElementContext !== null

      let requestBody: FormData | string
      let headers: Record<string, string>

      if (hasSignedBlobIds || hasElementContext || messageType) {
        const formData = new FormData()
        formData.append('message[content]', content)
        signedBlobIds.forEach(signedBlobId => {
          formData.append('message[signed_blob_ids][]', signedBlobId)
        })

        // Add selected element context if present - JSON encode to preserve structure
        if (hasElementContext) {
          formData.append('message[selected_element_context_json]', JSON.stringify(selectedElementContext))
        }

        // Add message type if specified
        if (messageType) {
          formData.append('message[message_type]', messageType)
        }

        requestBody = formData
        headers = {
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      } else {
        requestBody = JSON.stringify({ message: { content } })
        headers = {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      }

      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/messages`, {
        method: 'POST',
        headers,
        body: requestBody
      })

      if (!response.ok) {
        // Parse error response to get specific error messages
        const errorData = await response.json()

        // Check for insufficient credits error
        if (errorData.error === 'insufficient_credits' && errorData.message) {
          throw new Error(errorData.message)
        }

        // Check for validation errors from the service
        if (errorData.errors && errorData.errors.length > 0) {
          throw new Error(errorData.errors[0])
        }

        throw new Error('Failed to send message')
      }

      const data = await response.json()

      // Update optimistic message with server data including the real ID
      // IMPORTANT: We MUST update the ID so it matches the server-assigned ID
      // This is critical for features like undo that reference messages by ID
      // Keep optimistic attachments - server attachments may have 404 on variants
      setMessages(prev => prev.map(m =>
        m.id === optimisticId
          ? {
              ...m,
              id: data.message.id, // Use real server ID
              status: 'completed'
            }
          : m
      ))
    } catch (error) {
      console.error('Failed to send message:', error)

      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== optimisticId))

      // Hide processing indicator
      setProcessing(false)

      // Show appropriate error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message'
      const isCreditsError = errorMessage.includes('credits')
      const isProcessingError = errorMessage.includes('processing')

      if (isCreditsError) {
        // For credits errors, show the custom backend message
        toast.error('Insufficient Credits', {
          description: errorMessage
        })
      } else if (isProcessingError) {
        // For concurrent processing errors, keep processing=true (message IS processing)
        // and show the specific message
        setProcessing(true)
        toast.error('Message in Progress', {
          description: errorMessage
        })
      } else {
        // For other errors, show generic error
        toast.error('Failed to send message', {
          description: 'Please try again.'
        })
      }
      throw error
    } finally {
      setIsSendingMessage(false)
    }
  }, [conversation.can_accept_messages, processing, isNonBlockingWorkflowActive])

  /**
   * Accept changes (git operation)
   */
  const acceptChanges = useCallback(async () => {
    if (!conversation.can_accept_changes) return

    setIsAcceptingChanges(true)

    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/accept_changes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) {
        throw new Error('Failed to accept changes')
      }

      const data = await response.json()
      setConversation(data.conversation)
    } catch (error) {
      console.error('Failed to accept changes:', error)
      setIsAcceptingChanges(false)
      toast.error('Failed to save feature', {
        description: 'Please try again or contact support if the problem persists.'
      })
      throw error
    }
  }, [conversation.can_accept_changes])

  /**
   * Discard changes (git operation)
   */
  const discardChanges = useCallback(async () => {
    if (!conversation.can_discard_changes) return

    setIsDiscardingChanges(true)

    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/discard_changes`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) {
        throw new Error('Failed to discard changes')
      }

      const data = await response.json()
      setConversation(data.conversation)
    } catch (error) {
      console.error('Failed to discard changes:', error)
      setIsDiscardingChanges(false)
      toast.error('Failed to discard feature', {
        description: 'Please try again or contact support if the problem persists.'
      })
      throw error
    }
  }, [conversation.can_discard_changes])

  /**
   * Approve plan
   */
  const approvePlan = useCallback(async (messageId: number) => {
    // Show processing indicator immediately (optimistic)
    setProcessing(true)

    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/messages/${messageId}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) {
        throw new Error('Failed to approve plan')
      }

      const data = await response.json()
      setConversation(data.conversation)

      // Update the message with the full data from response (includes updated conversation level)
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId
            ? data.message
            : m
        )
      )
    } catch (error) {
      setProcessing(false)
      console.error('Failed to approve plan:', error)
      throw error
    }
  }, [])

  /**
   * Switch conversation mode
   */
  const switchToMode = useCallback(async (newMode: string) => {
    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ conversation: { mode: newMode } })
      })

      if (!response.ok) {
        throw new Error('Failed to switch mode')
      }

      const data = await response.json()
      setConversation(data.conversation)
    } catch (error) {
      console.error('Failed to switch mode:', error)
      toast.error('Failed to switch mode')
    }
  }, [])

  const toggleMode = useCallback(async () => {
    const newMode = conversation.mode === 'discussion' ? 'implementation' : 'discussion'
    await switchToMode(newMode)
  }, [conversation.mode, switchToMode])


  /**
   * Interrupt processing (stop button or workflow abort)
   */
  const interruptProcessing = useCallback(async () => {
    const workflowActive = checkWorkflowInProgress(activeWorkflow)

    // Allow interrupt if processing OR workflow in progress
    if ((!processing && !workflowActive) || isInterrupting) return

    setIsInterrupting(true)

    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/interrupt_processing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) {
        throw new Error('Failed to interrupt processing')
      }

      // Response received - webhook will send interrupt_complete event
      console.log('🛑 Interrupt request sent')
    } catch (error) {
      console.error('Failed to interrupt processing:', error)
      setIsInterrupting(false)
      toast.error('Failed to stop processing', {
        description: 'Please try again.'
      })
      throw error
    }
  }, [processing, isInterrupting, messages])

  /**
   * Retry workflow (works for all failure modes)
   * - revert: re-runs all steps
   * - pause: re-runs failed + pending steps
   * - continue: re-runs failed steps only
   */
  const retryWorkflow = useCallback(async () => {
    setIsRetryingWorkflow(true)
    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/workflow/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) {
        setIsRetryingWorkflow(false)
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to retry workflow')
      }

      console.log('🔄 Retry workflow request sent')
      // isRetryingWorkflow will be reset when processing_status: true arrives
    } catch (error) {
      setIsRetryingWorkflow(false)
      console.error('Failed to retry workflow:', error)
      toast.error('Failed to retry', {
        description: error instanceof Error ? error.message : 'Please try again.'
      })
      throw error
    }
  }, [])

  /**
   * Submit answers to clarifying questions
   */
  const submitAnswers = useCallback(async (messageId: number, answers: Record<string, string | string[]>) => {
    // Show processing indicator immediately (optimistic)
    setProcessing(true)

    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/messages/${messageId}/submit_answers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ answers })
      })

      if (!response.ok) {
        throw new Error('Failed to submit answers')
      }

      const data = await response.json()

      // Update the questions message with stored answers
      if (data.message) {
        setMessages(prev =>
          prev.map(m =>
            m.id === messageId ? data.message : m
          )
        )
      }
    } catch (error) {
      setProcessing(false)
      console.error('Failed to submit answers:', error)
      toast.error('Failed to submit answers', {
        description: 'Please try again.'
      })
      throw error
    }
  }, [])

  /**
   * Confirm a confirmation button (sends the prompt as a follow-up message)
   */
  const confirmButton = useCallback(async (messageId: number, prompt: string | undefined, clickedLabel?: string) => {
    // Show processing indicator immediately (optimistic)
    setProcessing(true)

    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/messages/${messageId}/confirm_button`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ prompt, clicked_label: clickedLabel })
      })

      if (!response.ok) {
        throw new Error('Failed to confirm')
      }

      const data = await response.json()

      // Update the confirmation message to show it was clicked
      if (data.message) {
        setMessages(prev =>
          prev.map(m =>
            m.id === messageId ? data.message : m
          )
        )
      }

      // Add the follow-up message to the list
      if (data.follow_up_message) {
        setMessages(prev => [...prev, data.follow_up_message])
      } else {
        // No follow-up (e.g., production cancel) — clear processing state
        setProcessing(false)
      }
    } catch (error) {
      setProcessing(false)
      console.error('Failed to confirm:', error)
      toast.error('Failed to confirm', {
        description: 'Please try again.'
      })
      throw error
    }
  }, [])

  /**
   * Refetch fresh conversation and project data
   * Used to catch missed broadcasts (e.g., title/name updates before WebSocket connected)
   * Pass force=true to bypass the processing guard (e.g., initial placeholder name fetch)
   */
  const refetchData = useCallback(async (force = false) => {
    // Don't refetch if we're in the middle of an operation (prevents race conditions)
    // Unless forced (e.g., initial connection with placeholder project name)
    if (!force && (processing || isSendingMessage || isAcceptingChanges || isDiscardingChanges || isInterrupting)) {
      console.log('⏸️ Skipping refetch - operation in progress')
      return
    }

    // Debounce - don't refetch if we just did (prevents rapid successive calls)
    const now = Date.now()
    if (now - lastRefetchRef.current < REFETCH_COOLDOWN) {
      console.log('⏸️ Skipping refetch - too soon after last refetch')
      return
    }
    lastRefetchRef.current = now

    try {
      const response = await fetch(`/projects/${projectIdRef.current}/features/${conversationIdRef.current}/refresh`, {
        headers: {
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) {
        throw new Error('Failed to refetch data')
      }

      const data = await response.json()

      // Update conversation and project with fresh data
      if (data.conversation) {
        setConversation(prev => ({ ...prev, ...data.conversation }))
      }

      if (data.project) {
        setProject(data.project)
      }

      // Sync processing state from database (fixes stuck state on reconnect)
      // But don't overwrite optimistic processing=true state
      if (data.hasProcessingMessages !== undefined && !processing) {
        setProcessing(data.hasProcessingMessages)
        // Seed default activity so the indicator shows "Building..." instead of "Thinking..."
        if (data.hasProcessingMessages && activityHistory.length === 0) {
          setActivityHistory(['Building'])
        }
      }

      console.log('🔄 Fresh data loaded:', { conversation: data.conversation?.title, project: data.project?.name, processing: data.hasProcessingMessages })
    } catch (error) {
      console.error('Failed to refetch data:', error)
      // Don't show error to user - this is a background operation
    }
  }, [processing, isSendingMessage, isAcceptingChanges, isDiscardingChanges, isInterrupting])

  /**
   * Load older messages (pagination)
   * Fetches messages before the oldest currently loaded message using timestamp-based cursor pagination
   */
  const loadOlderMessages = useCallback(async () => {
    if (!hasMoreMessages || isLoadingOlder) return

    setIsLoadingOlder(true)

    try {
      const oldestMessage = messages[0]
      if (!oldestMessage) {
        setHasMoreMessages(false)
        return
      }

      const response = await fetch(
        `/api/projects/${projectIdRef.current}/messages?before=${encodeURIComponent(oldestMessage.created_at)}&per_page=50`,
        {
          headers: {
            'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to load older messages')
      }

      const data = await response.json()

      // Prepend older messages to the beginning of the array
      setMessages(prev => [...data.messages, ...prev])
      setHasMoreMessages(data.has_more)

      console.log(`📜 Loaded ${data.messages.length} older messages, has_more: ${data.has_more}`)
    } catch (error) {
      console.error('Failed to load older messages:', error)
      toast.error('Failed to load older messages', {
        description: 'Please try again.'
      })
    } finally {
      setIsLoadingOlder(false)
    }
  }, [messages, hasMoreMessages, isLoadingOlder])

  return {
    // State
    conversation,
    messages,
    groupedTodos,
    processing,
    currentActivity,
    activityHistory,
    isSendingMessage,
    isAcceptingChanges,
    isDiscardingChanges,
    isInterrupting,
    isRetryingWorkflow,
    project,
    conversations,
    historyEntries,
    organization,
    hasMoreMessages,
    isLoadingOlder,
    nextStepSuggestions,
    collapsedMessageIds,
    expandedTurns,
    canUndoMessageId,
    isRewinding,
    orphanedMessageIds,
    liveCredentials,
    liveProductSpec,
    pmBrowserScreenshots,
    memoryWarning,
    dismissMemoryWarning: () => setMemoryWarning(null),
    codingWorkers,
    provisioningWorkers,

    // Actions
    addProvisioningWorkers,
    removeProvisioningWorkers,
    handleChannelEvent,
    handleProjectEvent,
    handleUndo,
    setIsRewinding, // For HistoryTab rewind
    sendMessage,
    acceptChanges,
    discardChanges,
    approvePlan,
    submitAnswers,
    confirmButton,
    toggleMode,
    switchToMode,
    interruptProcessing,
    retryWorkflow,
    refetchData,
    loadOlderMessages,
    toggleTurnExpansion: (userMessageId: number) => {
      setExpandedTurns(prev => {
        const newSet = new Set(prev)
        if (newSet.has(userMessageId)) {
          newSet.delete(userMessageId)
        } else {
          newSet.add(userMessageId)
        }
        return newSet
      })
    },
    setProject,
    startOptimisticWorkflow: (workflowName: string) => {
      setActiveWorkflow({
        id: -1,
        workflow_name: workflowName,
        description: workflowName === 'deploy_to_production' ? 'Deploying project to production...' : workflowName,
        status: 'processing',
        steps_data: [],
        started_at: new Date().toISOString(),
      })
    },
    activeWorkflow
  }
}
